// src/pages/admin/AdminAnalytics.tsx
//
// M&E (Monitoring & Evaluation) analytics dashboard.
//
// ACCESS:
//   admin / committee_chair — full access to all stats
//   all others              — access wall (router should also guard this)
//
// ENDPOINTS CONSUMED:
//   GET /api/admin/stats          — useGetAdminStatsQuery
//     → totalMentees, activeMentees, tierDistribution,
//       activeCycle, tasks, library, fellows, announcements
//
//   GET /api/admin/stats/mentees  — useGetAdminMenteeStatsQuery (per quarter)
//     → submissions breakdown by status × quarter
//     → activity breakdown by type × quarter
//     → profiles aggregate: avgOverallScore, avgTasksSubmitted, avgContentViewed, totalProfiles
//
//   GET /api/admin/users          — useGetUsersQuery (for pagination/mentee count live)
//
// SECTIONS:
//   1. Key Metrics         — 8 top-level stat tiles
//   2. Membership          — tier distribution bar + active vs total
//   3. Programme Progress  — per-quarter submission funnel (not-started → submitted → graded)
//   4. Engagement          — per-quarter activity breakdown table
//   5. Content Health      — tasks + library published vs draft
//   6. Legacy Fellows      — total nominated vs selected ratio bar
//   7. M&E Snapshot        — programme-wide averages (score, tasks, content views)

import React, { useState, useMemo } from "react";
import {
  BarChart2,
  Users,
  TrendingUp,
  BookOpen,
  Star,
  Megaphone,
  ClipboardList,
  Eye,
  Activity,
  RefreshCw,
  Info,
  Shield,
  ChevronDown,
  ChevronUp,
  Flame,
  Calendar,
} from "lucide-react";

import { useAppSelector } from "../../app/hooks";
import {
  useGetAdminStatsQuery,
  useGetAdminMenteeStatsQuery,
} from "../../app/api/adminApi";
//import type { AdminDashboardStats } from "../../app/api/adminApi";
import type { UserRole } from "../../app/api/authApi";
import type { QuarterKey } from "../../app/api/programmeApi";

/* ─────────────────────────────────────────────────────────────────
   ACCESS
───────────────────────────────────────────────────────────────── */
const CAN_VIEW_ANALYTICS: UserRole[] = [
  "admin",
  "committee_chair",
  "co",
  "cyp",
  "ryp",
];
const QUARTERS: QuarterKey[] = ["Q1", "Q2", "Q3", "Q4"];

/* ─────────────────────────────────────────────────────────────────
   PRIMITIVES
───────────────────────────────────────────────────────────────── */
const Bone: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
);

const fmtDate = (d?: string | null) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

/* ─────────────────────────────────────────────────────────────────
   STAT TILE
───────────────────────────────────────────────────────────────── */
const Tile: React.FC<{
  label: string;
  value: number | string;
  sub?: string;
  colour: string;
  icon: React.ReactNode;
  loading?: boolean;
  span2?: boolean;
}> = ({ label, value, sub, colour, icon, loading, span2 }) => (
  <div
    className={`bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-5 ${span2 ? "sm:col-span-2" : ""}`}
  >
    <div className="flex items-center gap-2 mb-2 text-[11px] text-gray-400">
      {icon}
      {label}
    </div>
    {loading ? (
      <Bone className="h-8 w-16" />
    ) : (
      <p className={`text-3xl font-bold ${colour}`}>{value}</p>
    )}
    {sub && <p className="text-[11px] text-gray-400 mt-1">{sub}</p>}
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   PROGRESS BAR
───────────────────────────────────────────────────────────────── */
const Bar: React.FC<{
  label: string;
  value: number;
  total: number;
  colour: string;
  showPct?: boolean;
}> = ({ label, value, total, colour, showPct = true }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1 text-xs">
        <span className="text-gray-600 font-medium">{label}</span>
        <span className="text-gray-500">
          {value.toLocaleString()}
          {showPct && ` (${pct}%)`}
        </span>
      </div>
      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${colour} transition-all duration-700`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   SECTION WRAPPER
───────────────────────────────────────────────────────────────── */
const Section: React.FC<{
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  collapsible?: boolean;
}> = ({ title, subtitle, icon, children, collapsible }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div
        className={`flex items-center justify-between px-5 py-4 border-b border-gray-100 ${collapsible ? "cursor-pointer hover:bg-gray-50 transition-colors" : ""}`}
        onClick={collapsible ? () => setOpen((v) => !v) : undefined}
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600 shrink-0">
            {icon}
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900">{title}</p>
            {subtitle && (
              <p className="text-[11px] text-gray-400">{subtitle}</p>
            )}
          </div>
        </div>
        {collapsible && (
          <div className="text-gray-400">
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        )}
      </div>
      {(!collapsible || open) && <div className="px-5 py-5">{children}</div>}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   HELPERS to parse raw mentee-stats API data
───────────────────────────────────────────────────────────────── */

type RawSubmission = {
  _id: { quarter: string; status: string };
  count: number;
};
type RawActivity = { _id: { quarter: string; type: string }; count: number };

// Build a map: quarter → { status: count }
const buildSubmissionMap = (raw: RawSubmission[]) => {
  const m: Record<string, Record<string, number>> = {};
  raw.forEach(({ _id, count }) => {
    if (!m[_id.quarter]) m[_id.quarter] = {};
    m[_id.quarter][_id.status] = count;
  });
  return m;
};

// Build a map: quarter → { type: count }
const buildActivityMap = (raw: RawActivity[]) => {
  const m: Record<string, Record<string, number>> = {};
  raw.forEach(({ _id, count }) => {
    if (!m[_id.quarter]) m[_id.quarter] = {};
    m[_id.quarter][_id.type] = count;
  });
  return m;
};

const TASK_STATUSES = [
  "not_started",
  "in_progress",
  "submitted",
  "graded",
] as const;
const STATUS_META: Record<
  string,
  { label: string; colour: string; dot: string }
> = {
  not_started: {
    label: "Not started",
    colour: "text-gray-500",
    dot: "bg-gray-300",
  },
  in_progress: {
    label: "In progress",
    colour: "text-blue-600",
    dot: "bg-blue-400",
  },
  submitted: {
    label: "Submitted",
    colour: "text-amber-600",
    dot: "bg-amber-400",
  },
  graded: { label: "Graded", colour: "text-green-600", dot: "bg-green-500" },
};

const ACTIVITY_LABELS: Record<string, string> = {
  task_started: "Tasks started",
  task_submitted: "Tasks submitted",
  task_graded: "Tasks graded",
  content_viewed: "Content views",
  content_downloaded: "Downloads",
  fellows_nominated: "Nominations",
  fellows_selected: "Fellows selected",
  tier_upgraded: "Tier upgrades",
  discussion_posted: "Discussion posts",
};

/* ─────────────────────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────────────────────── */
const AdminAnalytics: React.FC = () => {
  const user = useAppSelector((s) => s.auth.user)!;
  const role = (user.role ?? "committee_member") as UserRole;
  const canView = CAN_VIEW_ANALYTICS.includes(role);

  const [selectedQ, setSelectedQ] = useState<QuarterKey | "all">("all");

  const {
    data: statsRes,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useGetAdminStatsQuery(undefined, { skip: !canView });

  const {
    data: menteeStatsRes,
    isLoading: menteeLoading,
    refetch: refetchMentee,
  } = useGetAdminMenteeStatsQuery(
    selectedQ !== "all" ? { quarter: selectedQ } : {},
    { skip: !canView },
  );

  const stats = statsRes?.data;
  const mStats = menteeStatsRes?.data as any;
  const loading = statsLoading || menteeLoading;

  // Parse mentee stats
  const subMap = useMemo(
    () => (mStats?.submissions ? buildSubmissionMap(mStats.submissions) : {}),
    [mStats],
  );
  const actMap = useMemo(
    () => (mStats?.activity ? buildActivityMap(mStats.activity) : {}),
    [mStats],
  );
  const profiles = mStats?.profiles ?? {
    avgOverallScore: null,
    avgTasksSubmitted: 0,
    avgContentViewed: 0,
    totalProfiles: 0,
  };

  // Which quarters to show in tables (all 4, or just the selected one)
  const quartersToShow: QuarterKey[] =
    selectedQ === "all" ? QUARTERS : [selectedQ];

  // All activity types that appeared
  const allActivityTypes = useMemo(() => {
    const types = new Set<string>();
    Object.values(actMap).forEach((q) =>
      Object.keys(q).forEach((t) => types.add(t)),
    );
    return Array.from(types).sort();
  }, [actMap]);

  const isLoading = statsLoading;

  /* Access gate */
  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center gap-3 pb-12">
        <div className="w-14 h-14 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center">
          <Shield size={22} className="text-gray-300" />
        </div>
        <p className="text-sm font-semibold text-gray-600">Access restricted</p>
        <p className="text-xs text-gray-400 max-w-xs">
          Analytics are available to Admin and Committee Chair only.
        </p>
      </div>
    );
  }

  /* ──────────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-6 pb-12">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-purple-600 mb-1">
            Monitoring & Evaluation
          </p>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">
            Programme-wide engagement, progression, and impact metrics.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Quarter filter — affects mentee stats section */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
            {(["all", ...QUARTERS] as const).map((q) => (
              <button
                key={q}
                onClick={() => setSelectedQ(q as QuarterKey | "all")}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all
                  ${selectedQ === q ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              >
                {q === "all" ? "All" : q}
              </button>
            ))}
          </div>
          <button
            onClick={() => {
              refetchStats();
              refetchMentee();
            }}
            disabled={loading}
            className="p-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw
              size={14}
              className={
                loading ? "animate-spin text-gray-400" : "text-gray-500"
              }
            />
          </button>
        </div>
      </div>

      {/* ── SECTION 1: KEY METRICS ─────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile
          label="Total mentees"
          value={stats?.users.totalMentees ?? 0}
          sub={`${stats?.users.activeMentees ?? 0} active`}
          colour="text-purple-700"
          icon={<Users size={13} className="text-purple-400" />}
          loading={isLoading}
        />
        <Tile
          label="Active cycle"
          value={stats?.programme.activeCycle?.quarter ?? "—"}
          sub={stats?.programme.activeCycle?.theme ?? "No active quarter"}
          colour="text-green-700"
          icon={<Calendar size={13} className="text-green-400" />}
          loading={isLoading}
        />
        <Tile
          label="Published tasks"
          value={stats?.tasks.published ?? 0}
          sub={`${stats?.tasks.draft ?? 0} drafts`}
          colour="text-blue-700"
          icon={<ClipboardList size={13} className="text-blue-400" />}
          loading={isLoading}
        />
        <Tile
          label="Library items"
          value={stats?.library.published ?? 0}
          sub={`${stats?.library.draft ?? 0} drafts`}
          colour="text-orange-700"
          icon={<BookOpen size={13} className="text-orange-400" />}
          loading={isLoading}
        />
        <Tile
          label="Legacy Fellows"
          value={stats?.fellows.total ?? 0}
          sub={`${stats?.fellows.selected ?? 0} selected`}
          colour="text-amber-700"
          icon={<Star size={13} className="text-amber-400" />}
          loading={isLoading}
        />
        <Tile
          label="Announcements"
          value={stats?.announcements.published ?? 0}
          colour="text-red-700"
          icon={<Megaphone size={13} className="text-red-400" />}
          loading={isLoading}
        />
        <Tile
          label="Avg. score"
          value={
            profiles.avgOverallScore != null
              ? `${Number(profiles.avgOverallScore).toFixed(1)}/10`
              : "—"
          }
          sub="Across all graded tasks"
          colour="text-green-700"
          icon={<TrendingUp size={13} className="text-green-400" />}
          loading={menteeLoading}
        />
        <Tile
          label="Profiles tracked"
          value={profiles.totalProfiles ?? 0}
          sub="Mentee journey profiles"
          colour="text-purple-700"
          icon={<Activity size={13} className="text-purple-400" />}
          loading={menteeLoading}
        />
      </div>

      {/* ── SECTION 2: MEMBERSHIP ──────────────────────────────────── */}
      <Section
        title="Membership"
        subtitle="Tier distribution and activation"
        icon={<Users size={16} />}
      >
        {isLoading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <Bone key={i} className="h-8" />
            ))}
          </div>
        ) : stats ? (
          <div className="space-y-4">
            {/* Tier distribution */}
            <div className="space-y-3">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">
                Tier breakdown
              </p>
              <Bar
                label="Wider Audience"
                value={stats.users.tierDistribution.wider_audience}
                total={stats.users.totalMentees}
                colour="bg-green-400"
              />
              <Bar
                label="Inner Circle"
                value={stats.users.tierDistribution.inner_circle}
                total={stats.users.totalMentees}
                colour="bg-blue-400"
              />
              <Bar
                label="Legacy Fellows"
                value={stats.users.tierDistribution.legacy_fellows}
                total={stats.users.totalMentees}
                colour="bg-purple-400"
              />
            </div>

            <div className="h-px bg-gray-100" />

            {/* Active vs total */}
            <div className="space-y-3">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">
                Activation
              </p>
              <Bar
                label="Active mentees"
                value={stats.users.activeMentees}
                total={stats.users.totalMentees}
                colour="bg-green-500"
              />
              <p className="text-[11px] text-gray-400">
                {stats.users.totalMentees - stats.users.activeMentees} inactive
                accounts
              </p>
            </div>

            {/* Visual tier pills */}
            <div className="flex flex-wrap gap-3 pt-2">
              {[
                {
                  label: "Wider Audience",
                  count: stats.users.tierDistribution.wider_audience,
                  colour: "bg-green-100  text-green-700",
                },
                {
                  label: "Inner Circle",
                  count: stats.users.tierDistribution.inner_circle,
                  colour: "bg-blue-100   text-blue-700",
                },
                {
                  label: "Legacy Fellows",
                  count: stats.users.tierDistribution.legacy_fellows,
                  colour: "bg-purple-100 text-purple-700",
                },
              ].map((t) => (
                <div
                  key={t.label}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl ${t.colour}`}
                >
                  <p className="text-lg font-bold">{t.count}</p>
                  <p className="text-[11px] font-semibold">{t.label}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </Section>

      {/* ── SECTION 3: SUBMISSION FUNNEL ───────────────────────────── */}
      <Section
        title="Submission Pipeline"
        subtitle={
          selectedQ === "all"
            ? "All quarters — task lifecycle by status"
            : `${selectedQ} — task lifecycle`
        }
        icon={<ClipboardList size={16} />}
        collapsible
      >
        {menteeLoading ? (
          <Bone className="h-48" />
        ) : Object.keys(subMap).length === 0 ? (
          <div className="text-center py-8">
            <ClipboardList size={24} className="text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">
              No submission data for the selected period.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[400px]">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left pb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400 pr-4">
                    Quarter
                  </th>
                  {TASK_STATUSES.map((s) => (
                    <th
                      key={s}
                      className="text-right pb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400 px-3"
                    >
                      <span className={STATUS_META[s].colour}>
                        {STATUS_META[s].label}
                      </span>
                    </th>
                  ))}
                  <th className="text-right pb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400 pl-3">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {quartersToShow.map((q) => {
                  const row = subMap[q] ?? {};
                  const total = Object.values(row).reduce((a, b) => a + b, 0);
                  if (total === 0 && selectedQ === "all") return null;
                  return (
                    <tr key={q} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 pr-4">
                        <span className="font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded text-xs">
                          {q}
                        </span>
                      </td>
                      {TASK_STATUSES.map((s) => (
                        <td key={s} className="py-3 px-3 text-right">
                          <span
                            className={`text-sm font-semibold ${row[s] ? STATUS_META[s].colour : "text-gray-300"}`}
                          >
                            {(row[s] ?? 0).toLocaleString()}
                          </span>
                        </td>
                      ))}
                      <td className="py-3 pl-3 text-right">
                        <span className="text-sm font-bold text-gray-700">
                          {total.toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Graded rate summary */}
            {Object.keys(subMap).length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-4">
                {quartersToShow.map((q) => {
                  const row = subMap[q] ?? {};
                  const graded = row["graded"] ?? 0;
                  const sub = row["submitted"] ?? 0;
                  const total = graded + sub;
                  const rate =
                    total > 0 ? Math.round((graded / total) * 100) : null;
                  if (!total) return null;
                  return (
                    <div key={q} className="text-[11px]">
                      <span className="font-bold text-purple-700 mr-1">
                        {q}
                      </span>
                      <span className="text-gray-500">grading rate: </span>
                      <span
                        className={`font-semibold ${rate !== null && rate >= 80 ? "text-green-600" : rate !== null && rate >= 50 ? "text-amber-600" : "text-red-500"}`}
                      >
                        {rate !== null ? `${rate}%` : "—"}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </Section>

      {/* ── SECTION 4: ACTIVITY BREAKDOWN ─────────────────────────── */}
      <Section
        title="Engagement Activity"
        subtitle="Actions by type per quarter"
        icon={<Activity size={16} />}
        collapsible
      >
        {menteeLoading ? (
          <Bone className="h-48" />
        ) : allActivityTypes.length === 0 ? (
          <div className="text-center py-8">
            <Activity size={24} className="text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">
              No activity data for the selected period.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[400px]">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left pb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400 pr-4 min-w-40">
                    Activity
                  </th>
                  {quartersToShow.map((q) => (
                    <th
                      key={q}
                      className="text-right pb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400 px-3"
                    >
                      {q}
                    </th>
                  ))}
                  <th className="text-right pb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400 pl-3">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {allActivityTypes.map((type) => {
                  const rowTotals = quartersToShow.map(
                    (q) => actMap[q]?.[type] ?? 0,
                  );
                  const total = rowTotals.reduce((a, b) => a + b, 0);
                  if (total === 0) return null;
                  return (
                    <tr
                      key={type}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-3 pr-4 text-xs text-gray-600 font-medium">
                        {ACTIVITY_LABELS[type] ?? type.replace(/_/g, " ")}
                      </td>
                      {rowTotals.map((v, i) => (
                        <td key={i} className="py-3 px-3 text-right">
                          <span
                            className={`text-sm font-semibold ${v > 0 ? "text-gray-700" : "text-gray-300"}`}
                          >
                            {v.toLocaleString()}
                          </span>
                        </td>
                      ))}
                      <td className="py-3 pl-3 text-right">
                        <span className="text-sm font-bold text-purple-700">
                          {total.toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* ── SECTION 5: CONTENT HEALTH ─────────────────────────────── */}
      <Section
        title="Content Health"
        subtitle="Tasks & library publish rates"
        icon={<BookOpen size={16} />}
      >
        {isLoading ? (
          <div className="space-y-4">
            {[0, 1, 2, 3].map((i) => (
              <Bone key={i} className="h-8" />
            ))}
          </div>
        ) : stats ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Tasks */}
            <div className="space-y-3">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                <ClipboardList size={11} /> Tasks
              </p>
              <Bar
                label="Published"
                value={stats.tasks.published}
                total={stats.tasks.total}
                colour="bg-purple-400"
              />
              <Bar
                label="Draft"
                value={stats.tasks.draft}
                total={stats.tasks.total}
                colour="bg-amber-300"
              />
              <div className="flex items-center justify-between text-[11px] text-gray-400 pt-1">
                <span>{stats.tasks.total} total</span>
                <span
                  className={`font-semibold ${stats.tasks.draft > 0 ? "text-amber-600" : "text-green-600"}`}
                >
                  {stats.tasks.draft > 0
                    ? `${stats.tasks.draft} unpublished`
                    : "All published ✓"}
                </span>
              </div>
            </div>
            {/* Library */}
            <div className="space-y-3">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                <BookOpen size={11} /> Library
              </p>
              <Bar
                label="Published"
                value={stats.library.published}
                total={stats.library.total}
                colour="bg-blue-400"
              />
              <Bar
                label="Draft"
                value={stats.library.draft}
                total={stats.library.total}
                colour="bg-amber-300"
              />
              <div className="flex items-center justify-between text-[11px] text-gray-400 pt-1">
                <span>{stats.library.total} total</span>
                <span
                  className={`font-semibold ${stats.library.draft > 0 ? "text-amber-600" : "text-green-600"}`}
                >
                  {stats.library.draft > 0
                    ? `${stats.library.draft} unpublished`
                    : "All published ✓"}
                </span>
              </div>
            </div>
          </div>
        ) : null}
      </Section>

      {/* ── SECTION 6: LEGACY FELLOWS ─────────────────────────────── */}
      <Section
        title="Legacy Fellows"
        subtitle="Nominations to selection ratio"
        icon={<Star size={16} />}
      >
        {isLoading ? (
          <Bone className="h-24" />
        ) : stats ? (
          <div className="space-y-4">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-amber-600">
                  {stats.fellows.total}
                </p>
                <p className="text-[11px] text-gray-400">Nominated</p>
              </div>
              <div className="flex-1 h-px bg-gray-100 relative">
                <div
                  className="absolute inset-y-0 left-0 bg-green-400 rounded-full transition-all duration-700"
                  style={{
                    width:
                      stats.fellows.total > 0
                        ? `${(stats.fellows.selected / stats.fellows.total) * 100}%`
                        : "0%",
                  }}
                />
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">
                  {stats.fellows.selected}
                </p>
                <p className="text-[11px] text-gray-400">Selected</p>
              </div>
            </div>
            {stats.fellows.total > 0 && (
              <p className="text-[11px] text-gray-500 text-center">
                {Math.round(
                  (stats.fellows.selected / stats.fellows.total) * 100,
                )}
                % selection rate
                {stats.fellows.total - stats.fellows.selected > 0
                  ? ` · ${stats.fellows.total - stats.fellows.selected} pending / declined`
                  : ""}
              </p>
            )}
            {stats.fellows.total === 0 && (
              <p className="text-[11px] text-gray-400 text-center">
                No nominations submitted yet.
              </p>
            )}
          </div>
        ) : null}
      </Section>

      {/* ── SECTION 7: M&E SNAPSHOT ───────────────────────────────── */}
      <Section
        title="M&E Programme Snapshot"
        subtitle="Aggregate averages across all mentee profiles"
        icon={<BarChart2 size={16} />}
      >
        {menteeLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <Bone key={i} className="h-20" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              {
                label: "Avg. task score",
                value:
                  profiles.avgOverallScore != null
                    ? `${Number(profiles.avgOverallScore).toFixed(1)}/10`
                    : "—",
                sub: "Across all graded submissions",
                colour: "text-green-700",
                icon: <TrendingUp size={14} className="text-green-400" />,
              },
              {
                label: "Avg. tasks submitted",
                value:
                  profiles.avgTasksSubmitted != null
                    ? Number(profiles.avgTasksSubmitted).toFixed(1)
                    : "—",
                sub: "Per mentee profile",
                colour: "text-blue-700",
                icon: <ClipboardList size={14} className="text-blue-400" />,
              },
              {
                label: "Avg. content views",
                value:
                  profiles.avgContentViewed != null
                    ? Number(profiles.avgContentViewed).toFixed(1)
                    : "—",
                sub: "Per mentee profile",
                colour: "text-purple-700",
                icon: <Eye size={14} className="text-purple-400" />,
              },
            ].map((m) => (
              <div key={m.label} className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-1.5 mb-2 text-[11px] text-gray-400">
                  {m.icon}
                  {m.label}
                </div>
                <p className={`text-2xl font-bold ${m.colour}`}>{m.value}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">{m.sub}</p>
              </div>
            ))}
          </div>
        )}

        {/* Active cycle detail */}
        {stats?.programme.activeCycle && (
          <div className="mt-5 pt-5 border-t border-gray-100">
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Flame size={11} className="text-green-500" /> Active quarter
            </p>
            <div className="bg-gradient-to-r from-purple-50 to-purple-100/50 rounded-xl px-4 py-3 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-bold text-purple-900">
                  {stats.programme.activeCycle.theme}
                </p>
                <p className="text-[11px] text-purple-600 mt-0.5">
                  {stats.programme.activeCycle.quarter}
                </p>
              </div>
              {stats.programme.activeCycle.eventDate && (
                <div className="text-right shrink-0">
                  <p className="text-[11px] text-gray-500">Event date</p>
                  <p className="text-xs font-semibold text-gray-700">
                    {fmtDate(stats.programme.activeCycle.eventDate)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Note about filter scope */}
        {selectedQ !== "all" && (
          <div className="mt-4 flex items-center gap-2 text-[11px] text-blue-600 bg-blue-50 rounded-lg px-3 py-2">
            <Info size={12} className="shrink-0" />
            Submission pipeline and activity data filtered to{" "}
            <strong>{selectedQ}</strong>. M&E snapshot averages are
            programme-wide.
          </div>
        )}
      </Section>
    </div>
  );
};

export default AdminAnalytics;
